/**
 * Variantendimensionen und Varianten eines Produktmodells.
 *
 * Diese Ansicht traegt die fachliche Aussage, um die es in diesem Block geht:
 *
 *   Eine Variante ist eine technische Identitaet.
 *   Eine SKU ist eine kaufmaennische.
 *   Beide fallen NICHT zwangslaeufig zusammen.
 *
 * Daraus folgt die Darstellung. Eine Variante ohne Artikeldatensatz erscheint
 * als „bestellbar, noch nicht angelegt" -- sichtbar anders als eine SKU, aber
 * ausdruecklich NICHT als Fehler oder Luecke (PRODUCT_DATA_MODEL.md 3.4).
 *
 * Und sie erscheint niemals wie ein ERP-Artikel: eine virtuelle Variante hat
 * keine Artikelnummer, keinen Bestand und keinen DTE-Status -- weil es sie
 * als kaufmaennische Einheit nicht gibt.
 */
import { useState } from "react";
import type {
  DimensionValue, ModelDimension, ModelVariant, PropertyDefinition,
  ProductModelDetail, VariantValueRef,
} from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { formatNumber, unitSuffix } from "./ProductProperties.tsx";
/* DASSELBE WORT WIE IN LISTE UND AKTE -- eine Aussage darf es nur einmal
   geben, und sie steht bereits an genau einer Stelle. */
import { verfuegbarkeitswort } from "../lib/verfuegbarkeit.ts";
import "./ProductModels.css";

/** Das Bleistiftzeichen -- dieselbe Konvention wie bei Texten und Merkmalen. */
const PENCIL = "✎";

/**
 * Ein Dimensions- oder Variantenwert als Text.
 *
 * Reine Darstellung. Der Wert wird NICHT umgerechnet und nicht gerundet --
 * NUMERIC kommt als Zeichenkette an und bleibt eine (Architektur 8.1).
 */
export function valueLabel(
  i18n: I18n,
  v: { num: string | null; num2?: string | null; min: string | null; max: string | null;
       bool: boolean | null; code: string | null; text: string | null },
  unit: string | null,
): string {
  const suffix = unitSuffix(unit);
  if (v.code !== null) return i18n.tCode("propValue", v.code, v.code);
  if (v.text !== null) return v.text;
  if (v.bool !== null) return i18n.t(v.bool ? "common.yes" : "common.no");
  /*
   * Zwei Richtungen derselben Optik: `30° × 70°`. Das Malzeichen steht
   * bewusst da, wo sonst der Gedankenstrich stuende -- `30–70` hiesse „30
   * bis 70 Grad" und waere eine andere Aussage (Migration 0045).
   */
  if (v.num !== null && v.num2) return `${v.num}${suffix} × ${v.num2}${suffix}`;
  if (v.num !== null) return `${v.num}${suffix}`;
  if (v.min !== null && v.max !== null) {
    // Ein Bereich mit gleicher unterer und oberer Grenze ist ein Punktwert.
    return v.min === v.max ? `${v.min}${suffix}` : `${v.min} – ${v.max}${suffix}`;
  }
  if (v.min !== null) return `≥ ${v.min}${suffix}`;
  if (v.max !== null) return `≤ ${v.max}${suffix}`;
  return "—";
}

// ── Variantendimensionen ───────────────────────────────────────────────────

export function ModelDimensions({ model, definitions, mayWrite, onChanged, onError }: {
  model: ProductModelDetail;
  definitions: readonly PropertyDefinition[];
  mayWrite: boolean;
  onChanged: (m: ProductModelDetail) => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [adding, setAdding] = useState(false);
  const [definition, setDefinition] = useState("");

  const used = new Set(model.dimensions.map((d) => d.definition));
  // Eine Achse verweist auf eine bestehende Merkmalsdefinition -- es entsteht
  // kein zweiter Merkmalsbegriff.
  const available = definitions.filter((d) => !used.has(d.code));

  async function add(): Promise<void> {
    if (!definition) return;
    try { onChanged(await api.addDimension(model.id, { definition })); setAdding(false); setDefinition(""); }
    catch (e) { onError(e); }
  }

  return (
    <section className="models__block">
      <h3 className="detail__section">{t("models.dimensions")}</h3>
      <p className="models__note">{t("models.dimensionsNote")}</p>

      {model.dimensions.length === 0 && <p className="models__empty">{t("models.noDimensions")}</p>}

      {model.dimensions.map((d) => (
        <DimensionRow key={d.id} dimension={d} definitions={definitions} mayWrite={mayWrite}
                      onChanged={onChanged} onError={onError} />
      ))}

      {mayWrite && available.length > 0 && (
        adding ? (
          <div className="models__form">
            <select className="list__select" value={definition} aria-label={t("models.dimensionProperty")}
                    onChange={(e) => setDefinition(e.target.value)}>
              <option value="">{t("models.choose")}</option>
              {available.map((d) => (
                <option key={d.code} value={d.code}>{i18n.tCode("prop", d.code, d.code)}</option>
              ))}
            </select>
            <button className="btn btn--primary" onClick={add} disabled={!definition}>{t("common.save")}</button>
            <button className="btn" onClick={() => { setAdding(false); setDefinition(""); }}>{t("common.cancel")}</button>
          </div>
        ) : (
          <button className="btn models__add" onClick={() => setAdding(true)}>{t("models.addDimension")}</button>
        )
      )}
    </section>
  );
}

function DimensionRow({ dimension, definitions, mayWrite, onChanged, onError }: {
  dimension: ModelDimension;
  definitions: readonly PropertyDefinition[];
  mayWrite: boolean;
  onChanged: (m: ProductModelDetail) => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [adding, setAdding] = useState(false);
  const def = definitions.find((d) => d.code === dimension.definition);

  async function removeDimension(): Promise<void> {
    if (!window.confirm(t("models.removeDimensionConfirm"))) return;
    try { onChanged(await api.deleteDimension(dimension.id)); } catch (e) { onError(e); }
  }

  async function removeValue(v: DimensionValue): Promise<void> {
    if (!window.confirm(t("models.removeValueConfirm"))) return;
    try { onChanged(await api.deleteDimensionValue(v.id)); } catch (e) { onError(e); }
  }

  return (
    <div className="models__dimension">
      <div className="models__dimhead">
        <strong>{i18n.tCode("prop", dimension.definition, dimension.definition)}</strong>
        {dimension.unit && <span className="models__unit">{dimension.unit}</span>}
        {mayWrite && (
          <button className="btn btn--quiet" onClick={removeDimension}
                  aria-label={t("models.removeDimension")}>{t("models.removeDimension")}</button>
        )}
      </div>

      {dimension.values.length === 0
        ? <p className="models__empty">{t("models.noValues")}</p>
        : (
          <ul className="models__values">
            {dimension.values.map((v) => (
              <li key={v.id} className="models__value">
                <span className="models__valuelabel">{valueLabel(i18n, v, dimension.unit)}</span>
                {/* Wie viele Varianten diesen Wert tragen. 0 heisst: der Wert
                    ist zulaessig, aber noch von keiner Variante belegt. */}
                <span className="models__valuecount"
                      title={t("models.valueVariantsTitle", { count: v.variantCount })}>
                  {v.variantCount}
                </span>
                {mayWrite && (
                  <button className="btn btn--quiet" onClick={() => removeValue(v)}
                          aria-label={t("models.removeValue")}>×</button>
                )}
              </li>
            ))}
          </ul>
        )}

      {mayWrite && def && (
        adding
          ? <DimensionValueForm dimension={dimension} def={def}
              onDone={(m) => { setAdding(false); if (m) onChanged(m); }} onError={onError} />
          : <button className="btn models__add" onClick={() => setAdding(true)}>{t("models.addValue")}</button>
      )}
    </div>
  );
}

/**
 * Eingabe eines zulaessigen Dimensionswerts.
 *
 * Die Eingabeform folgt dem Wertetyp des Merkmals -- dieselbe Typisierung wie
 * bei einem Merkmalswert. Ein Enum-Merkmal bekommt seinen Wertevorrat als
 * Auswahl; erfunden wird nichts.
 */
function DimensionValueForm({ dimension, def, onDone, onError }: {
  dimension: ModelDimension;
  def: PropertyDefinition;
  onDone: (m: ProductModelDetail | null) => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [num, setNum] = useState("");
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  const [bool, setBool] = useState(false);
  const [code, setCode] = useState("");
  const [text, setText] = useState("");
  /*
   * DIE ZWEITE OPTISCHE RICHTUNG.
   *
   * Die Wertform des Merkmals bestimmt, WAS messbar ist. Sie darf nicht
   * bestimmen, WIE nach einer Ausfuehrung gefragt wird. `beam_angle` steht
   * im Katalog als Bereich -- weil ein Zoomstrahler einen hat. Gefragt wurde
   * deshalb auch auf einer Achse nach „von" und „bis", und die Antwort auf
   * „welche Ausfuehrungen gibt es?" lautete `>= 85°`. Das war die falsche
   * Frage, nicht die falsche Antwort.
   *
   * Gefragt wird jetzt nach dem Winkel -- und, freiwillig, nach dem zweiten.
   * `30 × 70` ist eine asymmetrische Optik, kein Bereich von 30 bis 70.
   */
  const [num2, setNum2] = useState("");

  async function save(): Promise<void> {
    const body: Record<string, unknown> = {};
    /* Die ACHSE folgt der Achsenform, nicht der Merkmalsform (Migration
       0045). Fuer jedes Merkmal ausser dem Abstrahlwinkel sind beide
       gleich. */
    switch (def.dimensionForm) {
      case "angle": body["num"] = num; if (num2) body["num2"] = num2; break;
      case "integer": case "decimal": body["num"] = num; break;
      case "range": if (min) body["min"] = min; if (max) body["max"] = max; break;
      case "boolean": body["bool"] = bool; break;
      case "enum": body["code"] = code; break;
      case "text": body["text"] = text; break;
    }
    try { onDone(await api.addDimensionValue(dimension.id, body)); }
    catch (e) { onError(e); onDone(null); }
  }

  return (
    <div className="models__form">
      {(def.dimensionForm === "integer" || def.dimensionForm === "decimal") && (
        <input className="list__search" value={num} onChange={(e) => setNum(e.target.value)}
               aria-label={t("models.valueNumber")} placeholder={t("models.valueNumber")} />
      )}
      {def.dimensionForm === "angle" && (
        <>
          <input className="list__search models__narrow" value={num} onChange={(e) => setNum(e.target.value)}
                 aria-label={t("models.valueAngleX")} placeholder={t("models.valueAngleX")} />
          <input className="list__search models__narrow" value={num2} onChange={(e) => setNum2(e.target.value)}
                 aria-label={t("models.valueAngleY")} placeholder={t("models.valueAngleY")} />
        </>
      )}
      {def.dimensionForm === "range" && (
        <>
          <input className="list__search models__narrow" value={min} onChange={(e) => setMin(e.target.value)}
                 aria-label={t("models.valueMin")} placeholder={t("models.valueMin")} />
          <input className="list__search models__narrow" value={max} onChange={(e) => setMax(e.target.value)}
                 aria-label={t("models.valueMax")} placeholder={t("models.valueMax")} />
        </>
      )}
      {def.dimensionForm === "boolean" && (
        <label className="models__check">
          <input type="checkbox" checked={bool} onChange={(e) => setBool(e.target.checked)} />
          {t("common.yes")}
        </label>
      )}
      {def.dimensionForm === "enum" && (
        <select className="list__select" value={code} aria-label={t("models.valueCode")}
                onChange={(e) => setCode(e.target.value)}>
          <option value="">{t("models.choose")}</option>
          {def.enumValues.map((v) => (
            <option key={v.code} value={v.code}>{i18n.tCode("propValue", v.code, v.code)}</option>
          ))}
        </select>
      )}
      {def.dimensionForm === "text" && (
        <input className="list__search" value={text} onChange={(e) => setText(e.target.value)}
               aria-label={t("models.valueText")} placeholder={t("models.valueText")} />
      )}
      <button className="btn btn--primary" onClick={save}>{t("common.save")}</button>
      <button className="btn" onClick={() => onDone(null)}>{t("common.cancel")}</button>
    </div>
  );
}

// ── Varianten ──────────────────────────────────────────────────────────────

export function ModelVariantList({ model, mayWrite, onChanged, onError, onOpenArticle, hiddenDimensions, onAssign }: {
  model: ProductModelDetail;
  mayWrite: boolean;
  /**
   * Achsen, die hier nicht als Spalte stehen -- auf der Modellseite V2 die
   * GEMEINSAMEN (HB200-U: 85° an allen vier). Sie stehen einmal darueber,
   * statt viermal in der Tabelle. Bearbeitet werden sie weiterhin.
   */
  hiddenDimensions?: ReadonlySet<number>;
  /** Produktmodelle 002A: einer Ausfuehrung ohne Artikel einen zuordnen -- derselbe Dialog wie in der Matrix. */
  onAssign?: (variantId: number) => void;
  onChanged: (m: ProductModelDetail) => void;
  onError: (e: unknown) => void;
  onOpenArticle: (id: number) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [adding, setAdding] = useState(false);

  const materialised = model.variants.filter((v) => v.article !== null).length;
  const spalten = model.dimensions.filter((d) => !hiddenDimensions?.has(d.id));
  const virtual = model.variants.length - materialised;

  return (
    <section className="models__block">
      <h3 className="detail__section">{t("models.variants")}</h3>

      {/* Die Mischmaterialisierung in einer Zeile: wie viele Varianten das
          Modell fuehrt und wie viele davon kaufmaennisch angelegt sind. */}
      <p className="models__counts">
        <strong>{model.variants.length}</strong> {t("models.variantsCount")}
        {" · "}
        <strong>{materialised}</strong> {t("models.materialised")}
        {" · "}
        <strong>{virtual}</strong> {t("models.virtual")}
      </p>

      {model.variants.length === 0 && <p className="models__empty">{t("models.noVariants")}</p>}

      {model.variants.length > 0 && (
        <table className="models__table">
          <thead>
            <tr>
              <th>{t("models.colVariant")}</th>
              {spalten.map((d) => (
                <th key={d.id}>{i18n.tCode("prop", d.definition, d.definition)}</th>
              ))}
              {/*
                * PREIS UND VERFUEGBARKEIT STEHEN HIER, WEIL DIE FRAGE HIER
                * GESTELLT WIRD.
                *
                * „Welche Ausfuehrungen haben wir, was kosten sie und welche
                * sind verfuegbar?" ist eine Frage an das MODELL. Wer sie
                * bisher beantworten wollte, musste jede SKU einzeln
                * oeffnen -- bei drei Ausfuehrungen geht das, bei zwoelf
                * nicht mehr.
                *
                * Beide Angaben sind vorhanden; sie standen nur nicht
                * nebeneinander.
                */}
              <th>{t("models.colPrice")}</th>
              <th>{t("models.colAvailability")}</th>
              <th>{t("models.colFocus")}</th>
              <th>{t("models.colDte")}</th>
              {mayWrite && <th aria-label={t("models.colActions")} />}
            </tr>
          </thead>
          <tbody>
            {model.variants.map((v) => (
              <VariantRow key={v.id} variant={v} model={model} mayWrite={mayWrite} spalten={spalten} onAssign={onAssign}
                          onChanged={onChanged} onError={onError} onOpenArticle={onOpenArticle} />
            ))}
          </tbody>
        </table>
      )}

      {mayWrite && (
        adding
          ? <VariantForm model={model} onDone={(m) => { setAdding(false); if (m) onChanged(m); }} onError={onError} />
          : <button className="btn models__add" onClick={() => setAdding(true)}>{t("models.addVariant")}</button>
      )}
    </section>
  );
}

function VariantRow({ variant, model, mayWrite, spalten, onChanged, onError, onOpenArticle, onAssign }: {
  variant: ModelVariant;
  model: ProductModelDetail;
  spalten: readonly ProductModelDetail["dimensions"][number][];
  onAssign?: (variantId: number) => void;
  mayWrite: boolean;
  onChanged: (m: ProductModelDetail) => void;
  onError: (e: unknown) => void;
  onOpenArticle: (id: number) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [editing, setEditing] = useState(false);
  const byDimension = new Map<number, VariantValueRef>(variant.values.map((v) => [v.dimensionId, v]));

  async function remove(): Promise<void> {
    if (!window.confirm(t("models.removeVariantConfirm"))) return;
    try { onChanged(await api.deleteVariant(variant.id)); } catch (e) { onError(e); }
  }

  return (
    <>
      <tr className={variant.article === null ? "models__row--virtual" : undefined}>
        <td>
          {variant.article
            ? (
              // Der Weg Modell -> SKU.
              <button className="models__link" onClick={() => onOpenArticle(variant.article!.id)}>
                <code>{variant.article.articleNumber}</code>
              </button>
            )
            : (
              /* Eine virtuelle Variante hat KEINE Artikelnummer -- sie darf
                 auch nicht wie eine aussehen (sichtbare V1, Punkt 8). */
              <>
                <span className="models__virtual" title={t("models.virtualTitle")}>
                  {variant.label ?? t("models.virtualLabel")}
                </span>
                {variant.trashedArticle && (
                  <span className="mp__papierkorb">
                    {t("mp.inTrash", { number: variant.trashedArticle.articleNumber })}
                  </span>
                )}
                {onAssign && (
                  <button className="btn btn--small mp__zuordnen" data-testid="mv-zuordnen"
                    onClick={() => onAssign(variant.id)}>{t("mz.open")}</button>
                )}
              </>
            )}
        </td>
        {spalten.map((d) => {
          const v = byDimension.get(d.id);
          return <td key={d.id}>{v ? valueLabel(i18n, v, d.unit) : <span className="models__none">—</span>}</td>;
        })}
        {/*
          * Der Preis. `null` heisst hier ZWEIERLEI, und die Oberflaeche
          * behauptet nicht zu wissen, welches: entweder es gibt keine
          * Freigabe, oder dieses Konto darf sie nicht sehen. Beides ist
          * „—", und beides ist ehrlich. Ein „0,00 €" waere es nicht.
          */}
        <td className="models__price">
          {variant.article?.listPriceEur
            ? <span title={variant.article.listPriceValidFrom
                ? t("models.priceValidFrom", { date: variant.article.listPriceValidFrom })
                : undefined}>
                {formatNumber(variant.article.listPriceEur, i18n.lang)} €
              </span>
            : <span className="models__none">—</span>}
        </td>
        {/*
          * Die Verfuegbarkeit. Eine virtuelle Variante hat keine -- sie ist
          * bestellbar, aber nicht angelegt, und ueber ihren Bestand gibt es
          * nichts zu sagen.
          */}
        <td>
          {variant.article
            ? <span className={`models__avail models__avail--${variant.article.availability}`}>
                {verfuegbarkeitswort(variant.article.availability, t)}
              </span>
            : <span className="models__none">—</span>}
        </td>
        <td>
          {variant.focusRank === null
            ? <span className="models__none">—</span>
            : <span className="badge badge--focus" title={t("models.focusTitle")}>
                {t("models.focusRank", { rank: variant.focusRank })}
              </span>}
        </td>
        <td>
          {variant.article
            ? <span className={`models__dte models__dte--${variant.article.dteListingStatus}`}>
                {i18n.tCode("dte", variant.article.dteListingStatus)}
              </span>
            /* Nicht materialisiert heisst: kein DTE-Datum. Ausdruecklich nicht
               „nicht gelistet" -- es gibt nichts, was gelistet sein koennte. */
            : <span className="models__none" title={t("models.noDteTitle")}>—</span>}
        </td>
        {mayWrite && (
          <td className="models__actions">
            <button className="btn btn--quiet" onClick={() => setEditing(!editing)}
                    aria-label={t("models.editVariant")}>{PENCIL}</button>
            <button className="btn btn--quiet" onClick={remove}
                    aria-label={t("models.removeVariant")}>×</button>
          </td>
        )}
      </tr>
      {editing && (
        <tr>
          <td colSpan={spalten.length + (mayWrite ? 6 : 5)}>
            <VariantForm model={model} variant={variant}
              onDone={(m) => { setEditing(false); if (m) onChanged(m); }} onError={onError} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Anlegen und Aendern einer Variante.
 *
 * Die Formularfelder bilden die drei Entscheidungen ab, die eine Variante
 * ausmachen -- und keine vierte:
 *
 *   1. Welche Werte auf welchen Achsen?
 *   2. Materialisiert -- und wenn ja, mit welchem Artikel?
 *   3. Fokusrang -- gesetzt, niemals abgeleitet.
 *
 * Als SKU stehen ausschliesslich Artikel zur Auswahl, die diesem Modell
 * zugeordnet und keine Platzhalter sind. Ein Platzhalterdatensatz erscheint
 * hier gar nicht erst; der Server wiese ihn ohnehin ab.
 */
function VariantForm({ model, variant, onDone, onError }: {
  model: ProductModelDetail;
  variant?: ModelVariant;
  onDone: (m: ProductModelDetail | null) => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [values, setValues] = useState<Record<number, number | "">>(() => {
    const initial: Record<number, number | ""> = {};
    for (const d of model.dimensions) initial[d.id] = "";
    for (const v of variant?.values ?? []) initial[v.dimensionId] = v.valueId;
    return initial;
  });
  const [articleId, setArticleId] = useState<number | "">(variant?.article?.id ?? "");
  const [label, setLabel] = useState(variant?.label ?? "");
  const [focusRank, setFocusRank] = useState<string>(
    variant?.focusRank === null || variant?.focusRank === undefined ? "" : String(variant.focusRank));

  // Freie SKU-Kandidaten: dem Modell zugeordnet, kein Platzhalter, nicht
  // bereits Variante eines anderen Eintrags.
  const taken = new Set(model.variants.filter((v) => v.id !== variant?.id)
    .map((v) => v.article?.id).filter((x): x is number => typeof x === "number"));
  const candidates = model.otherArticles.filter((a) => !taken.has(a.id));

  async function save(): Promise<void> {
    const payload = {
      articleId: articleId === "" ? null : Number(articleId),
      label: label.trim() === "" ? null : label.trim(),
      values: model.dimensions
        .map((d) => ({ dimensionId: d.id, valueId: values[d.id] }))
        .filter((v): v is { dimensionId: number; valueId: number } => typeof v.valueId === "number"),
    };
    try {
      if (variant) {
        onDone(await api.updateVariant(variant.id, {
          ...payload,
          focusRank: focusRank.trim() === "" ? null : Number(focusRank),
        }));
      } else {
        onDone(await api.createVariant(model.id, payload));
      }
    } catch (e) { onError(e); onDone(null); }
  }

  return (
    <div className="models__form models__form--wide">
      {model.dimensions.map((d) => (
        <label key={d.id} className="models__field">
          <span className="models__fieldlabel">{i18n.tCode("prop", d.definition, d.definition)}</span>
          <select className="list__select" value={values[d.id] ?? ""}
                  onChange={(e) => setValues({ ...values, [d.id]: e.target.value === "" ? "" : Number(e.target.value) })}>
            <option value="">{t("models.choose")}</option>
            {d.values.map((v) => (
              <option key={v.id} value={v.id}>{valueLabel(i18n, v, d.unit)}</option>
            ))}
          </select>
        </label>
      ))}

      <label className="models__field">
        <span className="models__fieldlabel">{t("models.sku")}</span>
        <select className="list__select" value={articleId}
                onChange={(e) => setArticleId(e.target.value === "" ? "" : Number(e.target.value))}>
          {/* Der Materialisierungsschalter. Leer ist ein gueltiger Zustand. */}
          <option value="">{t("models.notMaterialised")}</option>
          {variant?.article && (
            <option value={variant.article.id}>{variant.article.articleNumber}</option>
          )}
          {candidates.map((a) => (
            <option key={a.id} value={a.id}>{a.articleNumber}</option>
          ))}
        </select>
      </label>

      <label className="models__field">
        <span className="models__fieldlabel">{t("models.variantLabel")}</span>
        <input className="list__search" value={label} onChange={(e) => setLabel(e.target.value)} />
      </label>

      {variant && (
        <label className="models__field">
          <span className="models__fieldlabel">{t("models.focus")}</span>
          <input className="list__search models__narrow" value={focusRank} inputMode="numeric"
                 placeholder={t("models.focusPlaceholder")}
                 onChange={(e) => setFocusRank(e.target.value)} />
        </label>
      )}

      <div className="models__formactions">
        <button className="btn btn--primary" onClick={save}>{t("common.save")}</button>
        <button className="btn" onClick={() => onDone(null)}>{t("common.cancel")}</button>
      </div>
      {variant && <p className="models__note">{t("models.focusNote")}</p>}
    </div>
  );
}
